import { addLeadingZero } from "./addLeadingZero";

/**
 * Format a date object for Marriott URL parameters (MM%2FDD%2FYYYY format)
 * @param {Object} date - Date object with month, day, year properties
 * @returns {string} - Formatted date string
 */
const formatDateForMarriott = (date) => {
  if (!date) return "";
  return `${addLeadingZero(date.month)}%2F${addLeadingZero(date.day)}%2F${date.year}`;
};

/**
 * Build a Marriott hotel search URL with all required parameters
 * @param {Object} options - Search options
 * @param {string} options.destination - Destination name (e.g., "Miami, FL")
 * @param {Object} options.dateRange - Date range with start and end date objects
 * @param {number} options.rooms - Number of rooms
 * @param {number} options.adults - Number of adults per room
 * @param {number[]} options.children - Array of children ages
 * @param {string} options.placeId - Google Places ID for the destination
 * @returns {string} - Full Marriott search URL
 */
export const buildMarriottSearchUrl = ({
  destination,
  dateRange,
  rooms,
  adults,
  children = [],
  placeId,
}) => {
  const fromDate = formatDateForMarriott(dateRange.start);
  const toDate = formatDateForMarriott(dateRange.end);
  const fromDateDefault = formatDateForMarriott(dateRange.start);
  const toDateDefault = formatDateForMarriott(dateRange.end);

  // Calculate length of stay
  const lengthOfStay =
    dateRange.start && dateRange.end
      ? Math.abs(dateRange.end.compare(dateRange.start))
      : 1;

  // Format destination for Marriott URL
  const encodedDestination = encodeURIComponent(destination);

  // Extract state/region from destination (assumes "City, State/Country" format)
  const destinationParts = destination.split(",");
  const mainText = destinationParts[0]?.trim() || destination;
  const stateProvince = destinationParts[1]?.trim() || "";

  // Build search parameters
  const params = new URLSearchParams({
    fromToDate_submit: toDate,
    fromDate: fromDate,
    toDate: toDate,
    toDateDefaultFormat: toDateDefault,
    fromDateDefaultFormat: fromDateDefault,
    flexibleDateSearch: "false",
    "t-start": fromDate,
    "t-end": toDate,
    lengthOfStay: lengthOfStay.toString(),
    childrenCountBox: `${children.length}+Children+Per+Room`,
    childrenCount: children.length.toString(),
    clusterCode: "none",
    useRewardsPoints: "false",
    isAdvanceSearch: "false",
    recordsPerPage: "20",
    "destinationAddress.type": "State_Province_Region",
    isInternalSearch: "true",
    vsInitialRequest: "false",
    searchType: "InCity",
    "destinationAddress.stateProvinceDisplayName": stateProvince,
    "destinationAddress.stateProvince": stateProvince,
    singleSearchAutoSuggest: "Unmatched",
    "destinationAddress.placeId": placeId || "",
    "for-hotels-nearme": "Near",
    "destinationAddress.country": "",
    "destinationAddress.address": encodedDestination,
    collapseAccordian: "is-hidden",
    singleSearch: "true",
    "destinationAddress.secondaryText": stateProvince,
    "destinationAddress.mainText": mainText,
    isTransient: "true",
    initialRequest: "false",
    flexibleDateSearchRateDisplay: "false",
    isSearch: "true",
    isRateCalendar: "false",
    "destinationAddress.destination": encodedDestination,
    isHideFlexibleDateCalendar: "false",
    roomCountBox: `${rooms}+Room${rooms > 1 ? "s" : ""}`,
    roomCount: rooms.toString(),
    guestCountBox: `${adults}+Adult${adults > 1 ? "s" : ""}+Per+Room`,
    numAdultsPerRoom: adults.toString(),
    deviceType: "desktop-web",
  });

  return `https://marriott.com/search/findHotels.mi?${params.toString()}`;
};
